import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../../../context/ToastContext';
import FmeaAiagVdaApi from '../../../services/FmeaAiagVdaApi';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { FormCard } from '../../../components/FormCard/FormCard';
import Input from '../../../components/Input/Input';
import { FileUploader, ExistingFile } from '../../../components/FileUploader/FileUploader';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';
import { Modal } from '../../../components/Modal/Modal';
import { Button } from '../../../components/Button/Button';

const Step1: React.FC = () => {
  const { fmeaId } = useParams<{ fmeaId: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translate } = useTranslation('form');
  const { plantId, userId } = useAuth();
  const translateWithFallback = useCallback((key: string, fallback: string): string => {
    const value = translate(key);
    return value === key ? fallback : value;
  }, [translate]);

  const stepDescriptionTitle = translateWithFallback(
    'step1DescriptionTitle',
    'Configuração do Projeto e Definição do Escopo'
  );
  const stepDescriptionText = translateWithFallback(
    'step1DescriptionText',
    'Defina o escopo, os objetivos e a equipe para sua análise FMEA. Essa etapa fundamental garante o alinhamento adequado do projeto e o envolvimento das partes interessadas.'
  );
  const stepBaseTitle = translate('step1PlanningPreparation');
  const stepHeaderTitle = `${stepBaseTitle}: ${stepDescriptionTitle}`;

  const [objective, setObjective] = useState('');
  const [scope, setScope] = useState('');
  const [team, setTeam] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);

  const [initialObjective, setInitialObjective] = useState('');
  const [initialScope, setInitialScope] = useState('');
  const [initialTeam, setInitialTeam] = useState('');

  const [formDirty, setFormDirty] = useState(false);
  const [filesDirty, setFilesDirty] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (!fmeaId) return;
    const loadData = async (): Promise<void> => {
      try {
        const data = await FmeaAiagVdaApi.GetStep1(fmeaId);
        setObjective(data.objective || '');
        setScope(data.scope || '');
        setTeam(data.team || '');
        setInitialObjective(data.objective || '');
        setInitialScope(data.scope || '');
        setInitialTeam(data.team || '');
        // Load step attachments
        try {
          const attRes = await FmeaAiagVdaApi.GetStep1Attachments(fmeaId);
          const attachments = Array.isArray(attRes) ? attRes : attRes?.data ?? [];
          setExistingFiles(attachments.map((att: any) => ({
            id: att.id,
            fileName: att.fileName,
            contentType: att.contentType,
            uploadedAt: att.uploadedAt,
            uploadedBy: att.uploadedBy,
          })));
        } catch (err: any) {
          displayToast('error', translate('error'), err?.message || '', false, 3000);
        }
      } catch (err: any) {
        displayToast('error', translate('error'), err?.message || '', false, 3000);
      }
    };
    loadData();
  }, [fmeaId, displayToast, translate]);

  useEffect(() => {
    if (objective !== initialObjective || scope !== initialScope || team !== initialTeam) {
      setFormDirty(true);
    } else {
      setFormDirty(false);
    }
  }, [objective, scope, team, initialObjective, initialScope, initialTeam]);

  // Track file changes
  const handleFilesChange = useCallback((newFiles: File[]): void => {
    setFiles(newFiles);
    setFilesDirty(newFiles.length > 0);
  }, []);

  const handleBack = useCallback((): void => {
    setShowCancelModal(true);
  }, []);

  const handleNext = useCallback(async (): Promise<void> => {
    if (!objective.trim() || !team.trim()) {
      displayToast('error', translate('error'), translate('fillInAllRequiredFields'), false, 3000);
      return;
    }

    if (formDirty) {
      try {
        await FmeaAiagVdaApi.UpdateStep1(fmeaId!, { objective, scope, team, completedBy: userId });
        displayToast('success', translate('success'), translate('stepSaved'), false, 3000);
      } catch {
        displayToast('error', translate('error'), translate('stepNotSaved'), false, 3000);
        return;
      }
    }

    if (filesDirty && files.length > 0) {
      if (!plantId) {
        displayToast('error', translate('error'), translate('missingPlantId') || 'PlantId required', false, 3000);
        return;
      }
      const uploadPromises = files.map(file => {
        const formData = new FormData();
        formData.append('PlantId', plantId);
        formData.append('File', file);
        return FmeaAiagVdaApi.UploadDocumentStep1(fmeaId!, formData)
          .then(() => ({ file, success: true }))
          .catch(() => ({ file, success: false }));
      });
      const results = await Promise.all(uploadPromises);
      const failed = results.filter(r => !r.success).map(r => r.file.name);
      if (failed.length === 0) {
        displayToast('success', translate('success'), translate('filesUploaded'), false, 3000);
      } else {
        displayToast('error', translate('error'), `${translate('fileUploadError')}: ${failed.join(', ')}`, false, 3000);
      }
    }

    navigate(`/fmeas/${fmeaId}/steps/2`);
  }, [objective, team, scope, formDirty, filesDirty, files, fmeaId, plantId, userId, navigate, displayToast, translate]);

  const disableNext = !objective.trim() || !team.trim();

  return (
    <>
      <StepWrapper
        title={stepHeaderTitle}
        description={stepDescriptionText}
        currentStep={1}
        onCancel={handleBack}
        cancelLabel={translate('Cancel') || 'Cancelar'}
        onNext={handleNext}
        nextLabel={translate('Next') || 'Próximo'}
        nextDisabled={disableNext}
      >
        <FormCard
          title={translateWithFallback('projectConfiguration', 'Configuração do Projeto')}
          icon="settings"
          numColumns={1}
          onSubmit={(e) => { e.preventDefault(); handleNext(); }}
        >
          <Input
            type="textArea"
            label={translate('objective')}
            value={objective}
            onChange={(val: string) => setObjective(val)}
            required
            placeholder={`${translate('objective')} *`}
          />
          <Input
            type="textArea"
            label={translate('scope')}
            value={scope}
            onChange={(val: string) => setScope(val)}
            placeholder={translate('scope')}
          />
          <Input
            type="text"
            label={translate('team')}
            value={team}
            onChange={(val: string) => setTeam(val)}
            required
            placeholder={`${translate('team')} *`}
          />
        </FormCard>

        <FormCard
          title={translateWithFallback('attachments', 'Anexos')}
          icon="attach_file"
          numColumns={1}
          onSubmit={(e) => e.preventDefault()}
        >
          <FileUploader
            files={files}
            onFilesChange={handleFilesChange}
            existingFiles={existingFiles}
            labels={{
              title: translateWithFallback('attachFiles', 'Anexar Arquivos'),
              choose: translateWithFallback('chooseFiles', 'Escolher arquivos'),
              dragAndDrop: translateWithFallback('dragAndDrop', 'Arraste arquivos aqui ou clique para selecionar'),
            }}
          />
        </FormCard>
      </StepWrapper>

      <Modal
        visible={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        header={translate('confirmation') || 'Confirmação'}
        size="sm"
        footer={
          <>
            <Button
              label={translate('No') || 'Não'}
              variant="outlined"
              severity="secondary"
              onClick={() => setShowCancelModal(false)}
            />
            <Button
              label={translate('Yes') || 'Sim'}
              severity="danger"
              onClick={() => navigate('/ListFamilyFmeas')}
            />
          </>
        }
      >
        <p style={{ margin: 0, color: 'var(--color-neutral-700, #374151)' }}>
          {translate('cancelCreationMessage')}
        </p>
      </Modal>
    </>
  );
};

export default Step1;